import { createContext, FC, PropsWithChildren, useContext, useEffect, useMemo } from 'react';

import { useRouter } from '@cvt/routing';
import { PermissionContext } from '@cvt/contexts';
import { initUserTracking } from '@cvt/tracking';

import { AuthContext } from '@modules/Auth/contexts';

import { useMatchedRoute } from '@shared/hooks/useMatchedRoute';

import { UserType } from '../../enums';

import { ContextProps } from './types';

export const defaultContext: ContextProps = {
  user: undefined,
  isAdmin: false,
  isCustomer: false,
};

const userPermissionList: CVT.Permission.PermissionKey[] = [
  'disciplines.list',
  'disciplines.view',
  'majors.list',
  'majors.view',
  'universities.list',
  'universities.view',
  'assertions.list',
  'assertions.view',
];

const adminPermissionList: CVT.Permission.PermissionKey[] = [
  'users.list',
  'users.view',
  'users.create',
  'users.edit',
  'users.delete',
];

export const UserContext = createContext(defaultContext);

export const UserProvider: FC<PropsWithChildren> = ({ children }) => {

  const { user, isLoggedIn } = useContext(AuthContext);
  const { updatePermissions } = useContext(PermissionContext);

  const router = useRouter();

  const isAdmin = useMemo(() => user?.type === UserType.Admin, [user]);
  const isCustomer = useMemo(() => user?.type === UserType.Customer, [user]);

  useEffect(() => {
    updatePermissions(userPermissionList, isLoggedIn);
  }, [updatePermissions, isLoggedIn]);

  useEffect(() => {
    updatePermissions(adminPermissionList, isAdmin);
  }, [updatePermissions, isAdmin]);

  const { match: isInOnboarding } = useMatchedRoute([router.onboarding.path]);

  useEffect(() => {
    const fields: (keyof Users.Crud)[] = ['firstName', 'lastName', 'location', 'wantedDegreeLevel'];

    if (user) {
      const missing = fields.some(field => !user[field]);
      
      if (isInOnboarding && !missing) {
        router.home.go();
      }
      if (!isInOnboarding && missing) {
        router.onboarding.go();
      }
    }
  }, [isInOnboarding, router, user]);

  useEffect(() => {
    if (user) {
      initUserTracking(user);
    }
  }, [user]);

  return (
    <UserContext.Provider
      value={{
        user,
        isAdmin,
        isCustomer,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const UserConsumer = UserContext.Consumer;
 